import { Fragment, type ReactNode } from 'react';
import { findLinks } from '@vav/client-core';

/**
 * Render a message's text with its URLs as links.
 *
 * The matching, and everything security-relevant about it, lives in
 * `client-core/linkify.ts`: shared by both clients so a link that opens on one
 * cannot be inert on the other, and free of JSX so it can be tested without a
 * renderer. This only decides how the pieces are drawn.
 *
 * Links open in a new context with `noopener`, so the page cannot reach back
 * through `window.opener` into a messenger.
 */
export function linkify(text: string): ReactNode {
  const links = findLinks(text);
  if (links.length === 0) return text;

  const parts: ReactNode[] = [];
  let cursor = 0;

  for (const link of links) {
    if (link.start > cursor) parts.push(text.slice(cursor, link.start));
    parts.push(
      <a
        className="link"
        href={link.href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        /* An address reads left to right inside a right-to-left sentence, and
           isolating it keeps the surrounding punctuation where it belongs. */
        dir="ltr"
        onClick={(event) => event.stopPropagation()}
      >
        {link.raw}
      </a>,
    );
    cursor = link.start + link.raw.length;
  }

  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts.map((node, index) => <Fragment key={index}>{node}</Fragment>);
}
